import { Col, Container, Row } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';

// types
import { Benefit } from '../Career/types';

type BenefitsProps = {
    benefits: Benefit[];
};

// Assign a unique accent color per card for variety
const cardAccents = [
    { bg: '#eef6ff', icon: '#3b82f6', border: '#dbeafe' },
    { bg: '#fdf2f8', icon: '#ec4899', border: '#fce7f3' },
    { bg: '#ecfdf5', icon: '#10b981', border: '#d1fae5' },
    { bg: '#fefce8', icon: '#f59e0b', border: '#fef3c7' },
    { bg: '#f5f3ff', icon: '#8b5cf6', border: '#ede9fe' },
    { bg: '#fff7ed', icon: '#f97316', border: '#ffedd5' },
];

const Benefits = ({ benefits }: BenefitsProps) => {
    return (
        <section
            className="position-relative overflow-hidden"
            style={{
                paddingTop: 'clamp(3rem, 6vw, 5rem)',
                paddingBottom: 'clamp(3rem, 6vw, 5rem)',
                background: '#fafbfc',
            }}
        >
            {/* Subtle background decoration */}
            <div
                style={{
                    position: 'absolute',
                    top: '-80px',
                    right: '-60px',
                    width: '300px',
                    height: '300px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(38,162,171,0.06) 0%, transparent 70%)',
                    pointerEvents: 'none',
                }}
            />

            <Container>
                {/* Section header */}
                <Row className="justify-content-center mb-5" data-aos="fade-up">
                    <Col lg={7} className="text-center">
                        <div
                            className="d-inline-flex align-items-center mb-3"
                            style={{
                                gap: '8px',
                                background: '#e8f7f8',
                                borderRadius: '50px',
                                padding: '6px 18px',
                            }}
                        >
                            <FeatherIcon icon="target" style={{ width: '14px', height: '14px', color: '#26A2AB' }} />
                            <span
                                style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    letterSpacing: '1.5px',
                                    textTransform: 'uppercase',
                                    color: '#26A2AB',
                                }}
                            >
                                Industries We Serve
                            </span>
                        </div>
                        <h2
                            style={{
                                fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                                fontWeight: 800,
                                color: '#1a1a2e',
                                lineHeight: 1.3,
                            }}
                        >
                            Target Industries
                        </h2>
                        <p style={{ color: '#6b7280', fontSize: '1rem', maxWidth: '480px', margin: '8px auto 0' }}>
                            Tailored CRM solutions for diverse business sectors
                        </p>
                    </Col>
                </Row>

                {/* Cards grid */}
                <Row className="justify-content-center g-4">
                    {(benefits || []).map((benefit, index) => {
                        const accent = cardAccents[index % cardAccents.length];
                        return (
                            <Col
                                xs={12}
                                sm={6}
                                lg={4}
                                key={index.toString()}
                                data-aos="fade-up"
                                data-aos-delay={index * 80}
                            >
                                <div
                                    className="h-100 d-flex flex-column align-items-center text-center p-4"
                                    style={{
                                        background: '#fff',
                                        borderRadius: '16px',
                                        border: `1px solid ${accent.border}`,
                                        transition: 'all 0.3s ease',
                                        cursor: 'default',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-5px)';
                                        e.currentTarget.style.boxShadow = `0 12px 32px rgba(0,0,0,0.06)`;
                                        e.currentTarget.style.borderColor = accent.icon;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = 'none';
                                        e.currentTarget.style.borderColor = accent.border;
                                    }}
                                >
                                    {/* Icon circle */}
                                    <div
                                        className="d-flex align-items-center justify-content-center mb-3"
                                        style={{
                                            width: '56px',
                                            height: '56px',
                                            borderRadius: '14px',
                                            background: accent.bg,
                                        }}
                                    >
                                        <FeatherIcon
                                            icon={benefit.icon}
                                            style={{ width: '24px', height: '24px', color: accent.icon }}
                                        />
                                    </div>

                                    {/* Title */}
                                    <h5
                                        style={{
                                            fontSize: '1.05rem',
                                            fontWeight: 700,
                                            color: '#1a1a2e',
                                            marginBottom: '8px',
                                        }}
                                    >
                                        {benefit.title}
                                    </h5>

                                    {/* Description */}
                                    <p
                                        className="mb-0"
                                        style={{
                                            fontSize: '0.88rem',
                                            color: '#6b7280',
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {benefit.description}
                                    </p>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            </Container>
        </section>
    );
};

export default Benefits;
